import React, { useMemo, useState, useEffect } from "react";
import { APIS, CATEGORIES, API_METADATA } from "./apiData";
import "./apiDocumentation.css";

const METHODS = ["GET", "POST", "PUT", "DELETE"];
const CONSUMERS = [
  { id: "all", label: "All" },
  { id: "mobile", label: "Mobile App" },
  { id: "web", label: "Admin Web" },
  { id: "both", label: "Both" },
  { id: "none", label: "Unused" },
];

const methodColor = (m) => {
  switch (m) {
    case "GET":
      return "apidoc-method-get";
    case "POST":
      return "apidoc-method-post";
    case "PUT":
      return "apidoc-method-put";
    case "DELETE":
      return "apidoc-method-delete";
    default:
      return "apidoc-method-other";
  }
};

const getConsumerType = (api) => {
  const mobile = (api.consumers?.mobile || []).length > 0;
  const web = (api.consumers?.web || []).length > 0;
  if (mobile && web) return "both";
  if (mobile) return "mobile";
  if (web) return "web";
  return "none";
};

const highlight = (text, query) => {
  if (!query || !text) return text;
  const safe = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = String(text).split(new RegExp(`(${safe})`, "gi"));
  return parts.map((p, i) =>
    p.toLowerCase() === query.toLowerCase() ? (
      <mark key={i} className="apidoc-mark">
        {p}
      </mark>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    )
  );
};

const ApiDocumentation = () => {
  const [query, setQuery] = useState("");
  const [activeMethods, setActiveMethods] = useState([]);
  const [category, setCategory] = useState("All");
  const [consumer, setConsumer] = useState("all");
  const [selectedId, setSelectedId] = useState(APIS[0]?.id || null);
  const [copyOk, setCopyOk] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return APIS.filter((api) => {
      if (activeMethods.length && !activeMethods.includes(api.method))
        return false;
      if (category !== "All" && api.category !== category) return false;
      if (consumer !== "all") {
        if (getConsumerType(api) !== consumer) return false;
      }
      if (!q) return true;
      const haystack = [
        api.path,
        api.method,
        api.title,
        api.description,
        api.category,
        api.controller,
        api.auth,
        ...(api.consumers?.mobile || []),
        ...(api.consumers?.web || []),
        ...(api.notes || []),
      ]
        .filter(Boolean)
        .join(" \u0001 ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [query, activeMethods, category, consumer]);

  const selected = useMemo(
    () => filtered.find((a) => a.id === selectedId) || filtered[0],
    [filtered, selectedId]
  );

  useEffect(() => {
    if (filtered.length && !filtered.find((a) => a.id === selectedId)) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId]);

  const toggleMethod = (m) => {
    setActiveMethods((prev) =>
      prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]
    );
  };

  const resetFilters = () => {
    setQuery("");
    setActiveMethods([]);
    setCategory("All");
    setConsumer("all");
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyOk(label || "Copied");
      setTimeout(() => setCopyOk(""), 1500);
    } catch {
      setCopyOk("Copy failed");
      setTimeout(() => setCopyOk(""), 1500);
    }
  };

  const stats = useMemo(() => {
    const total = APIS.length;
    const byMethod = METHODS.reduce(
      (acc, m) => ({ ...acc, [m]: APIS.filter((a) => a.method === m).length }),
      {}
    );
    const mobileOnly = APIS.filter((a) => getConsumerType(a) === "mobile").length;
    const webOnly = APIS.filter((a) => getConsumerType(a) === "web").length;
    const both = APIS.filter((a) => getConsumerType(a) === "both").length;
    const unused = APIS.filter((a) => getConsumerType(a) === "none").length;
    return { total, byMethod, mobileOnly, webOnly, both, unused };
  }, []);

  return (
    <div className="page-wrapper apidoc-page">
      <div className="content container-fluid">
        <div className="apidoc-header">
          <div>
            <h3 className="mb-1">API Explorer</h3>
            <p className="apidoc-subtitle">
              Browse, search and inspect every endpoint exposed by{" "}
              <strong>{API_METADATA.serviceName}</strong>. Use this catalog to
              understand what each API does, where it is consumed, and the
              auth / side-effects involved.
            </p>
          </div>
          <div className="apidoc-meta">
            <div>
              <span className="apidoc-meta-label">Dev base URL</span>
              <code>{API_METADATA.baseUrlDev}</code>
            </div>
            <div>
              <span className="apidoc-meta-label">Local</span>
              <code>{API_METADATA.baseUrlLocal}</code>
            </div>
            <div>
              <span className="apidoc-meta-label">Auth header</span>
              <code>{API_METADATA.authHeader}</code>
            </div>
          </div>
        </div>

        <div className="apidoc-stats">
          <div className="apidoc-stat">
            <span className="apidoc-stat-value">{stats.total}</span>
            <span className="apidoc-stat-label">Total endpoints</span>
          </div>
          {METHODS.map((m) => (
            <div key={m} className="apidoc-stat">
              <span
                className={`apidoc-method-pill ${methodColor(m)} apidoc-stat-pill`}
              >
                {m}
              </span>
              <span className="apidoc-stat-value">{stats.byMethod[m]}</span>
            </div>
          ))}
          <div className="apidoc-stat">
            <span className="apidoc-stat-value">{stats.mobileOnly}</span>
            <span className="apidoc-stat-label">Mobile only</span>
          </div>
          <div className="apidoc-stat">
            <span className="apidoc-stat-value">{stats.webOnly}</span>
            <span className="apidoc-stat-label">Web only</span>
          </div>
          <div className="apidoc-stat">
            <span className="apidoc-stat-value">{stats.both}</span>
            <span className="apidoc-stat-label">Used by both</span>
          </div>
          <div className="apidoc-stat">
            <span className="apidoc-stat-value">{stats.unused}</span>
            <span className="apidoc-stat-label">Server-only</span>
          </div>
        </div>

        <div className="apidoc-toolbar">
          <div className="apidoc-search">
            <i className="fa-solid fa-magnifying-glass apidoc-search-icon" />
            <input
              type="text"
              className="form-control apidoc-search-input"
              placeholder="Search by path, title, controller, screen, description…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button
                type="button"
                className="apidoc-search-clear"
                onClick={() => setQuery("")}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>

          <div className="apidoc-method-chips">
            {METHODS.map((m) => {
              const active = activeMethods.includes(m);
              return (
                <button
                  key={m}
                  type="button"
                  className={`apidoc-chip ${methodColor(m)} ${active ? "is-active" : ""}`}
                  onClick={() => toggleMethod(m)}
                >
                  {m}
                </button>
              );
            })}
          </div>

          <select
            className="form-select apidoc-select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="All">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <select
            className="form-select apidoc-select"
            value={consumer}
            onChange={(e) => setConsumer(e.target.value)}
          >
            {CONSUMERS.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            className="btn btn-outline-secondary apidoc-reset"
            onClick={resetFilters}
          >
            Reset
          </button>
        </div>

        <div className="apidoc-result-bar">
          Showing <strong>{filtered.length}</strong> of {APIS.length} endpoints
          {copyOk && <span className="apidoc-copy-toast">{copyOk}</span>}
        </div>

        <div className="apidoc-layout">
          <aside className="apidoc-list">
            {filtered.length === 0 && (
              <div className="apidoc-empty">
                <i className="fa-solid fa-magnifying-glass" />
                <p>No endpoints match your filters.</p>
              </div>
            )}
            {filtered.map((api) => {
              const ctype = getConsumerType(api);
              return (
                <button
                  key={api.id}
                  type="button"
                  className={`apidoc-list-item ${
                    selected && selected.id === api.id ? "is-selected" : ""
                  }`}
                  onClick={() => setSelectedId(api.id)}
                >
                  <div className="apidoc-list-row">
                    <span
                      className={`apidoc-method-pill ${methodColor(api.method)}`}
                    >
                      {api.method}
                    </span>
                    <span className="apidoc-list-path">
                      {highlight(api.path, query)}
                    </span>
                  </div>
                  <div className="apidoc-list-title">
                    {highlight(api.title || "", query)}
                  </div>
                  <div className="apidoc-list-meta">
                    <span className="apidoc-tag">{api.category}</span>
                    {api.fstBlocked && (
                      <span className="apidoc-tag apidoc-tag-warn">
                        FST blocked
                      </span>
                    )}
                    {ctype === "mobile" && (
                      <span className="apidoc-tag apidoc-tag-blue">Mobile</span>
                    )}
                    {ctype === "web" && (
                      <span className="apidoc-tag apidoc-tag-purple">Web</span>
                    )}
                    {ctype === "both" && (
                      <span className="apidoc-tag apidoc-tag-green">Both</span>
                    )}
                    {ctype === "none" && (
                      <span className="apidoc-tag apidoc-tag-grey">
                        Server only
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </aside>

          <section className="apidoc-detail">
            {!selected ? (
              <div className="apidoc-empty apidoc-empty-large">
                <i className="fa-solid fa-circle-info" />
                <p>Select an endpoint from the list to see details.</p>
              </div>
            ) : (
              <ApiDetail selected={selected} onCopy={copy} />
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

const ApiDetail = ({ selected, onCopy }) => {
  const ctype = getConsumerType(selected);
  return (
    <div className="apidoc-detail-inner">
      <header className="apidoc-detail-header">
        <div className="apidoc-detail-title-row">
          <span className={`apidoc-method-pill ${methodColor(selected.method)}`}>
            {selected.method}
          </span>
          <code className="apidoc-detail-path">{selected.path}</code>
          <button
            type="button"
            className="apidoc-copy-btn"
            onClick={() =>
              onCopy(`${selected.method} ${selected.path}`, "Endpoint copied")
            }
            title="Copy endpoint"
          >
            <i className="fa-solid fa-copy" />
          </button>
        </div>
        <h4 className="apidoc-detail-title">{selected.title || "—"}</h4>
        <div className="apidoc-detail-tags">
          <span className="apidoc-tag">{selected.category}</span>
          <span className="apidoc-tag apidoc-tag-auth">
            <i className="fa-solid fa-lock" /> {selected.auth}
          </span>
          {selected.fstBlocked && (
            <span className="apidoc-tag apidoc-tag-warn">
              FST sandbox blocked
            </span>
          )}
          {ctype !== "none" && (
            <span
              className={`apidoc-tag ${
                ctype === "mobile"
                  ? "apidoc-tag-blue"
                  : ctype === "web"
                  ? "apidoc-tag-purple"
                  : "apidoc-tag-green"
              }`}
            >
              {ctype === "mobile"
                ? "Mobile consumer"
                : ctype === "web"
                ? "Web consumer"
                : "Mobile + Web"}
            </span>
          )}
        </div>
      </header>

      {selected.description && (
        <Section title="Description">
          <p className="apidoc-detail-desc">{selected.description}</p>
        </Section>
      )}

      {selected.request && (
        <Section title="Request">
          <CodeBlock data={selected.request} />
        </Section>
      )}

      {selected.response && (
        <Section title="Sample response">
          <CodeBlock data={selected.response} />
        </Section>
      )}

      <Section title="cURL">
        <CurlBlock api={selected} onCopy={onCopy} />
      </Section>

      <Section title="Controller / handler">
        <code className="apidoc-inline-code">{selected.controller || "—"}</code>
      </Section>

      <div className="apidoc-consumer-grid">
        <Section title={`Used in Mobile App (gm-investment)`}>
          <ConsumerList items={selected.consumers?.mobile} emptyText="Not consumed by mobile app." />
        </Section>
        <Section title={`Used in Admin Web (inv-web)`}>
          <ConsumerList items={selected.consumers?.web} emptyText="Not consumed by admin web." />
        </Section>
      </div>

      {selected.notes && selected.notes.length > 0 && (
        <Section title="Notes / side-effects">
          <ul className="apidoc-notes">
            {selected.notes.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="apidoc-section">
    <h6 className="apidoc-section-title">{title}</h6>
    <div className="apidoc-section-body">{children}</div>
  </div>
);

const ConsumerList = ({ items = [], emptyText }) => {
  if (!items || items.length === 0) {
    return <p className="apidoc-empty-text">{emptyText}</p>;
  }
  return (
    <ul className="apidoc-consumer-list">
      {items.map((item, i) => (
        <li key={i}>
          <i className="fa-solid fa-angle-right" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
};

const CodeBlock = ({ data }) => {
  const text = typeof data === "string" ? data : JSON.stringify(data, null, 2);
  return <pre className="apidoc-code">{text}</pre>;
};

const CurlBlock = ({ api, onCopy }) => {
  const base = API_METADATA.baseUrlDev;
  const isPublic = /Public/i.test(api.auth || "");
  const lines = [
    `curl -X ${api.method} \\\n  "${base}${api.path.replace(/:[^/]+/g, "<param>")}"`,
  ];
  if (!isPublic) {
    lines.push('  -H "Authorization: <accessToken>"');
  }
  if (["POST", "PUT", "PATCH"].includes(api.method)) {
    lines.push('  -H "Content-Type: application/json"');
    lines.push("  -d '{ /* body */ }'");
  }
  const curl = lines.join(" \\\n");
  return (
    <div className="apidoc-curl-wrap">
      <pre className="apidoc-code">{curl}</pre>
      <button
        type="button"
        className="apidoc-copy-btn apidoc-curl-copy"
        onClick={() => onCopy(curl, "cURL copied")}
        title="Copy cURL"
      >
        <i className="fa-solid fa-copy" />
      </button>
    </div>
  );
};

export default ApiDocumentation;
